import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { FlexCenterWrapper } from "../../../../../components/DefaultNoDataWrapper";
import Text from "../../../../../components/Text";
import useDisclosure from "../../../../../hooks/use-disclosure";
import { useReduceDisclosure } from "../../../../../hooks/use-reduce-disclosure";
import PatientAssessmentFormModal from "../actions/PatientAssessmentFormModal";
import PatientIntakeFormModal from "../actions/PatientIntakeFormModal";
import ResolvePatientTask from "../../patient-task/ResolvePatientTask";
import ConfirmationModal from "../../../../../components/app/ConfirmationModal";
import ActionItem from "./ActionItem";
import { Action } from "../patient-portal-dashboard.types";
import ScrollableContainer from "./shared/ScrollableContainer";
import {
  FORM_TYPE_MAPPER,
  INTAKE_FORM_SECTIONS,
} from "../../../../../pages/app/provider/scheduling/appointment/appointment-constant";

type PatientActionOperation =
  "TASK" | "ASSESSMENT" | "INTAKE" | "CONSENT" | "UPLOAD";

const ACTION_BUTTON_MAP: Record<
  PatientActionOperation,
  { buttonText: string; buttonVariant: "outlined" | "contained" }
> = {
  INTAKE: { buttonText: "Complete", buttonVariant: "outlined" },
  ASSESSMENT: { buttonText: "Complete", buttonVariant: "outlined" },
  TASK: { buttonText: "Resolve Task", buttonVariant: "outlined" },
  CONSENT: { buttonText: "Sign Now", buttonVariant: "outlined" },
  UPLOAD: { buttonText: "Upload", buttonVariant: "outlined" },
};

const FORM_TOKEN_TO_LABEL = new Map<string, string>(
  INTAKE_FORM_SECTIONS.flatMap((s) => [
    [s.label.toLowerCase(), s.label],
    [s.value.toLowerCase(), s.label],
  ])
);

const isAllConsentForms = (forms?: string): boolean => {
  if (!forms) return false;
  const parts = forms
    .split(",")
    .map((f) => f.trim())
    .filter(Boolean);
  if (!parts.length) return false;
  return parts.every((f) => {
    const label = FORM_TOKEN_TO_LABEL.get(f.toLowerCase());
    return label ? FORM_TYPE_MAPPER[label] === "Consent" : false;
  });
};

interface DashboardActionsRequiredProps {
  actions: any[];
}

const DashboardActionsRequired: React.FC<DashboardActionsRequiredProps> = ({
  actions,
}) => {
  const queryClient = useQueryClient();
  const [selectedAction, setSelectedAction] = useState<any>();

  const refetchRequiringAction = () => {
    queryClient.invalidateQueries({ queryKey: ["patient-requiring-action"] });
  };

  const resolveTaskDisclosure = useDisclosure(false, {
    onClose: refetchRequiringAction,
  });

  const [intakeFormModalDisc, assessmentFormModalDisc, confirmResolveDisc] =
    useReduceDisclosure(3);

  const handleConfirmResolve = (taskId?: string) => {
    const resolvedTaskId = taskId ?? selectedAction?.taskId;
    resolveTaskDisclosure.open(resolvedTaskId ? [resolvedTaskId] : undefined);
    confirmResolveDisc.close();
  };

  const mapActionToItem = (event: any): Action => {
    const operation: PatientActionOperation =
      event?.patientRequiringActionOperation ||
      event?.requiringActionOperation ||
      "TASK";
    const effectiveOperation: PatientActionOperation =
      operation === "INTAKE" && isAllConsentForms(event?.forms)
        ? "CONSENT"
        : operation;
    const config =
      ACTION_BUTTON_MAP[effectiveOperation] ?? ACTION_BUTTON_MAP.TASK;

    return {
      uuid: event?.uuid,
      description: event?.description ?? "",
      buttonText: config.buttonText,
      buttonVariant: config.buttonVariant,
    };
  };

  const handleActionClick = (actionUuid: string) => {
    const event = actions.find((a) => a.uuid === actionUuid);
    if (!event) return;

    setSelectedAction(event);
    const operation =
      event?.patientRequiringActionOperation || event?.requiringActionOperation;

    switch (operation) {
      case "INTAKE": {
        intakeFormModalDisc.open({
          appointmentId: event?.appointmentId,
          patientId: event?.patient,
          intakeForms: event?.forms,
          // form ids, parallel (same order) to `forms` names; used to send the
          // correct id per form on submit in the patient portal.
          formEntries: event?.formEntries,
          actionId: event?.uuid,
        });
        return;
      }
      case "ASSESSMENT": {
        assessmentFormModalDisc.open({
          appointmentId: event?.appointmentId,
          patientId: event?.patient,
          assessmentType: event?.forms,
          actionId: event?.uuid,
        });
        return;
      }
      case "TASK": {
        handleConfirmResolve(event?.taskId);
        return;
      }
    }
  };

  return (
    <>
      <ScrollableContainer>
        {!actions?.length ? (
          <FlexCenterWrapper>
            <Text>No Actions Required found</Text>
          </FlexCenterWrapper>
        ) : (
          actions.map((event) => {
            const action = mapActionToItem(event);
            return (
              <ActionItem
                key={action.uuid}
                action={action}
                onActionClick={handleActionClick}
              />
            );
          })
        )}
      </ScrollableContainer>

      {intakeFormModalDisc.opened && (
        <PatientIntakeFormModal disclosureHost={intakeFormModalDisc} />
      )}

      {assessmentFormModalDisc.opened && (
        <PatientAssessmentFormModal disclosureHost={assessmentFormModalDisc} />
      )}

      {resolveTaskDisclosure.opened && (
        <ResolvePatientTask disclosureHost={resolveTaskDisclosure} />
      )}

      <ConfirmationModal
        maxWidth="sm"
        onConfirm={() => handleConfirmResolve(selectedAction?.taskId)}
        disclosureHost={confirmResolveDisc}
        confirmationText="Yes"
        cancelText="No"
        title={
          <Text fontWeight="medium" fontSize="24px">
            Do you want to resolve this task?
          </Text>
        }
      />
    </>
  );
};

export default DashboardActionsRequired;
